import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = (import.meta as any).env?.VITE_API_URL ? (import.meta as any).env.VITE_API_URL : 'http://localhost:3001';

interface Benchmark {
  id: string;
  platform: string;
  handle: string;
  url: string;
  type?: string;
  hashtags?: string[];
  createdAt: string;
}

interface Insight {
  id: string;
  account: string;
  postUrl: string;
  estimatedImpressions: number;
  estimatedEngagementRate: number;
  format: string;
  contentPillar?: string;
  hook: string;
  visualElements?: string;
  trendingAudio?: string;
  hashtags: string[];
  keyTakeaway: string;
  enhancedTakeaway?: string;
  createdAt: string;
}

interface ScheduleItem {
  id: string;
  date: string;
  platform: string;
  format: string;
  contentPillar: string;
  hook: string;
  visualElements: string;
  trendingAudio?: string;
  hashtags: string[];
  referencePostUrl: string;
  takeaway: string;
  status: string;
  createdAt: string;
}

interface Trend {
  id: string;
  type: string;
  value: string;
  name?: string;
  postCount: number;
  avgEngagement: number;
  detectedAt: string;
}

interface YouTubeVideo {
  id: string;
  benchmarkId?: string;
  platform: string;
  channelHandle?: string;
  channelUrl?: string;
  title: string;
  url: string;
  publishDate?: string;
  views?: number;
  likes?: number;
  comments?: number;
  duration?: string | number | null;
  description?: string;
  thumbnails?: any;
  transcript?: string;
  scrapedAt?: string;
}

interface Keyword {
  id: string;
  keyword: string;
  searchVolume: number;
  competition: string;
  difficulty: number;
  cpc: number;
  trend?: any;
  scrapedAt?: string;
}

interface NicheConfig {
  niche?: string;
  country?: string;
  updatedAt?: string;
}

export default function TrendLab() {
  const [activeTab, setActiveTab] = useState<'benchmarks' | 'insights' | 'schedule' | 'trends' | 'youtube' | 'keywords'>('keywords');
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [trends, setTrends] = useState<Trend[]>([]);
  const [youtubeVideos, setYouTubeVideos] = useState<YouTubeVideo[]>([]);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [nicheConfig, setNicheConfig] = useState<NicheConfig>({});
  const [nicheInput, setNicheInput] = useState('');
  const [countryInput, setCountryInput] = useState('US');
  const [loading, setLoading] = useState(false);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState<string>('');
  const [pipelineProgress, setPipelineProgress] = useState<string[]>([]);

  // New benchmark form
  const [newBenchmark, setNewBenchmark] = useState({
    platform: 'Instagram',
    handle: '',
    url: '',
    type: 'Direct',
    hashtags: '',
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'benchmarks') {
        const res = await axios.get(`${API_BASE}/api/strategy/benchmarks`, { withCredentials: true });
        setBenchmarks(res.data.benchmarks || []);
      } else if (activeTab === 'insights') {
        const res = await axios.get(`${API_BASE}/api/strategy/insights`, { withCredentials: true });
        setInsights(res.data.insights || []);
      } else if (activeTab === 'schedule') {
        const res = await axios.get(`${API_BASE}/api/strategy/schedule`, { withCredentials: true });
        setSchedule(res.data.schedule || []);
      } else if (activeTab === 'trends') {
        const res = await axios.get(`${API_BASE}/api/strategy/trends`, { withCredentials: true });
        setTrends(res.data.trends || []);
      } else if (activeTab === 'youtube') {
        const res = await axios.get(`${API_BASE}/api/strategy/youtube`, { withCredentials: true });
        setYouTubeVideos(res.data.videos || []);
      } else if (activeTab === 'keywords') {
        const [keywordsRes, configRes] = await Promise.all([
          axios.get(`${API_BASE}/api/strategy/keywords`, { withCredentials: true }),
          axios.get(`${API_BASE}/api/strategy/niche-config`, { withCredentials: true }),
        ]);
        setKeywords(keywordsRes.data.keywords || []);
        setNicheConfig(configRes.data.config || {});
        setNicheInput(configRes.data.config?.niche || '');
        setCountryInput(configRes.data.config?.country || 'US');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBenchmark = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const hashtagsArray = newBenchmark.hashtags
        .split(',')
        .map((h) => h.trim())
        .filter(Boolean);
      await axios.post(`${API_BASE}/api/strategy/benchmarks`, {
        platform: newBenchmark.platform,
        handle: newBenchmark.handle,
        url: newBenchmark.url,
        type: newBenchmark.type,
        hashtags: hashtagsArray,
      }, { withCredentials: true });
      setNewBenchmark({ platform: 'Instagram', handle: '', url: '', type: 'Direct', hashtags: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding benchmark:', error);
      alert('Failed to add benchmark');
    }
  };

  const handleRunPipeline = async () => {
    if (pipelineRunning) {
      alert('Pipeline is already running! Please wait for it to complete.');
      return;
    }

    if (!confirm('Start the full content strategy pipeline? This will:\n\n• Discover new keywords for your niche\n• Analyze competitor trends\n• Generate insights from benchmark accounts\n\nThis may take 2-5 minutes depending on your niche.')) {
      return;
    }

    setPipelineRunning(true);
    setPipelineStatus('Starting pipeline...');
    setPipelineProgress(['🔄 Initializing content strategy pipeline...']);

    try {
      // Start the pipeline
      const response = await axios.post(`${API_BASE}/api/strategy/run-pipeline`, {}, { withCredentials: true });

      if (response.data.success) {
        setPipelineStatus('Pipeline running in background...');
        setPipelineProgress(prev => [...prev, '🚀 Pipeline started successfully!', '⏳ Processing keyword research...']);

        // Start polling for status updates
        pollPipelineStatus();

        // Show initial success message
        alert('✅ Pipeline started!\n\nThe system is now:\n• Analyzing your niche\n• Discovering trending keywords\n• Scraping competitor data\n\nCheck the progress indicator above for real-time updates.');
      } else {
        throw new Error(response.data.error || 'Failed to start pipeline');
      }
    } catch (error) {
      console.error('Error running pipeline:', error);
      setPipelineStatus('❌ Pipeline failed to start');
      setPipelineProgress(prev => [...prev, '❌ Error starting pipeline']);
      alert('❌ Failed to start pipeline: ' + (error.response?.data?.error || error.message));
      setPipelineRunning(false);
    }
  };

  const pollPipelineStatus = () => {
    const pollInterval = setInterval(async () => {
      try {
        // Check if pipeline is still running by looking at keyword data updates
        const keywordsResponse = await axios.get(`${API_BASE}/api/strategy/keywords`, { withCredentials: true });

        if (keywordsResponse.data.success && keywordsResponse.data.keywords.length > 0) {
          setPipelineProgress(prev => [...prev, '✅ Keyword research completed!', '📊 Analyzing insights from competitor data...']);

          // Check insights data
          const insightsResponse = await axios.get(`${API_BASE}/api/strategy/insights`, { withCredentials: true });

          if (insightsResponse.data.success && insightsResponse.data.insights.length > 0) {
            setPipelineProgress(prev => [...prev, '✅ Insights analysis completed!', '📈 Processing trend data...']);

            // Check trends data
            const trendsResponse = await axios.get(`${API_BASE}/api/strategy/trends`, { withCredentials: true });

            if (trendsResponse.data.success && trendsResponse.data.trends.length > 0) {
              // Pipeline appears complete
              setPipelineStatus('✅ Pipeline completed successfully!');
              setPipelineProgress(prev => [...prev, '✅ All pipeline steps completed!', '🎉 Ready to generate trend-based calendars!']);
              setPipelineRunning(false);
              clearInterval(pollInterval);

              // Refresh data to show results
              setTimeout(() => fetchData(), 1000);

              alert('🎉 Pipeline completed!\n\nResults:\n• Keywords discovered\n• Insights analyzed\n• Trends identified\n\nYou can now generate trend-based content calendars!');
            }
          }
        }
      } catch (error) {
        // Continue polling, ignore individual API errors
        console.log('Polling pipeline status...');
      }
    }, 5000); // Check every 5 seconds

    // Stop polling after 5 minutes to prevent infinite polling
    setTimeout(() => {
      clearInterval(pollInterval);
      if (pipelineRunning) {
        setPipelineStatus('⚠️ Pipeline may still be running...');
        setPipelineProgress(prev => [...prev, '⏳ Pipeline taking longer than expected. Check again in a few minutes.']);
        setPipelineRunning(false);
      }
    }, 300000); // 5 minutes
  };

  const exportToCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((h) => JSON.stringify(row[h] || '')).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 relative">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2">📊 Trend Lab</h1>
        <p className="text-muted-foreground">
          Monitor competitors, analyze trends, and generate data-driven posting schedules.
        </p>
      </div>

      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-20 flex items-center justify-center cursor-not-allowed">
        <div className="bg-card border border-border p-8 rounded-xl shadow-2xl text-center max-w-md transform scale-110">
          <div className="text-5xl mb-4">🚧</div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent mb-2">
            Coming Soon
          </h2>
          <p className="text-muted-foreground text-lg mb-6">
            Our advanced Trend Lab with AI analysis and Real-World data scraping is currently being upgraded.
          </p>
          <button disabled className="px-6 py-3 bg-primary/20 text-primary font-semibold rounded-full cursor-not-allowed">
            Notify Me When Ready
          </button>
        </div>
      </div>

      {/* Content wrapped in a div to allow absolute positioning of overlay relative to this container */}
      <div className="relative opacity-40 pointer-events-none select-none filter blur-sm">

        {/* Pipeline Status Display */}
        {(pipelineRunning || pipelineStatus) && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-3 h-3 rounded-full ${pipelineRunning ? 'bg-primary animate-pulse' : 'bg-green-500'}`}></div>
              <h3 className="font-semibold text-primary">Pipeline Status</h3>
            </div>
            <p className="text-primary/80 mb-2">{pipelineStatus}</p>
            {pipelineProgress.length > 0 && (
              <div className="bg-card rounded p-3 border border-border max-h-32 overflow-y-auto">
                <ul className="text-sm text-foreground space-y-1">
                  {pipelineProgress.map((step, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-xs">•</span>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {pipelineRunning && (
              <div className="mt-3 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-primary">Processing... Please wait</span>
              </div>
            )}
          </div>
        )}

        <div className="mb-6 flex gap-4 items-center">
          <button
            onClick={handleRunPipeline}
            disabled={pipelineRunning}
            className="px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pipelineRunning ? '⏳ Running...' : '🚀 Run Full Pipeline'}
          </button>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80"
          >
            🔄 Refresh
          </button>
        </div>

        <div className="mb-6 border-b border-border">
          <nav className="flex gap-4">
            {['keywords', 'benchmarks', 'insights', 'schedule', 'trends', 'youtube'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`px-4 py-2 font-medium capitalize transition-colors ${activeTab === tab
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2 text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'keywords' && (
              <div>
                <div className="bg-card rounded-lg shadow p-6 mb-6 border border-border">
                  <h2 className="text-xl font-bold mb-4 text-foreground">Configure Your Niche</h2>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      try {
                        await axios.post(
                          `${API_BASE}/api/strategy/niche-config`,
                          { niche: nicheInput, country: countryInput },
                          { withCredentials: true }
                        );
                        alert('Niche saved! Run the pipeline to discover keywords.');
                        fetchData();
                      } catch (error) {
                        console.error('Error saving niche:', error);
                        alert('Failed to save niche');
                      }
                    }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  >
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Niche (e.g., "home decor", "lighting", "handmade ceramics")
                      </label>
                      <input
                        type="text"
                        value={nicheInput}
                        onChange={(e) => setNicheInput(e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Enter your niche..."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Country</label>
                      <select
                        value={countryInput}
                        onChange={(e) => setCountryInput(e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                        <option value="NZ">New Zealand</option>
                      </select>
                    </div>
                    <div className="md:col-span-3">
                      <button
                        type="submit"
                        className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                      >
                        💾 Save Niche
                      </button>
                    </div>
                  </form>
                  {nicheConfig.niche && (
                    <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                      <p className="text-sm text-foreground">
                        <strong className="text-primary">Current Niche:</strong> {nicheConfig.niche} ({nicheConfig.country})
                      </p>
                      {nicheConfig.updatedAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Updated: {new Date(nicheConfig.updatedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-card rounded-lg shadow overflow-hidden border border-border">
                  <div className="p-4 bg-muted/50 border-b border-border flex justify-between items-center">
                    <h2 className="text-lg font-bold text-foreground">Keyword Opportunities ({keywords.length})</h2>
                    <button
                      onClick={() => exportToCSV(keywords, 'keywords.csv')}
                      className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                    >
                      📥 Export CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-foreground">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-2 text-left">Keyword</th>
                          <th className="px-4 py-2 text-left">Search Volume</th>
                          <th className="px-4 py-2 text-left">Difficulty</th>
                          <th className="px-4 py-2 text-left">Competition</th>
                          <th className="px-4 py-2 text-left">CPC</th>
                          <th className="px-4 py-2 text-left">Opportunity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keywords
                          .sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0))
                          .map((kw) => {
                            const isHighOpportunity = (kw.searchVolume || 0) > 100 && (kw.difficulty || 0) < 50;
                            return (
                              <tr key={kw.id} className="border-t border-border hover:bg-muted/50">
                                <td className="px-4 py-2 font-medium">{kw.keyword}</td>
                                <td className="px-4 py-2">{kw.searchVolume?.toLocaleString() || '—'}</td>
                                <td className="px-4 py-2">
                                  <span
                                    className={`px-2 py-1 rounded text-xs ${(kw.difficulty || 0) < 30
                                      ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                      : (kw.difficulty || 0) < 50
                                        ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                                        : 'bg-red-500/10 text-red-600 dark:text-red-400'
                                      }`}
                                  >
                                    {kw.difficulty || 0}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-xs uppercase text-muted-foreground">{kw.competition || 'UNKNOWN'}</td>
                                <td className="px-4 py-2">${kw.cpc?.toFixed(2) || '0.00'}</td>
                                <td className="px-4 py-2">
                                  {isHighOpportunity && (
                                    <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-semibold">
                                      🎯 High
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'benchmarks' && (
              <div>
                <div className="bg-card rounded-lg shadow p-6 mb-6 border border-border">
                  <h2 className="text-xl font-bold mb-4 text-foreground">Add Benchmark Account</h2>
                  <form onSubmit={handleAddBenchmark} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Platform</label>
                      <select
                        value={newBenchmark.platform}
                        onChange={(e) => setNewBenchmark({ ...newBenchmark, platform: e.target.value })}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option>Instagram</option>
                        <option>TikTok</option>
                        <option>Magazine</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Handle</label>
                      <input
                        type="text"
                        value={newBenchmark.handle}
                        onChange={(e) => setNewBenchmark({ ...newBenchmark, handle: e.target.value })}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="@username or site name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">URL</label>
                      <input
                        type="url"
                        value={newBenchmark.url}
                        onChange={(e) => setNewBenchmark({ ...newBenchmark, url: e.target.value })}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="https://..."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                      <select
                        value={newBenchmark.type}
                        onChange={(e) => setNewBenchmark({ ...newBenchmark, type: e.target.value })}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option>Direct</option>
                        <option>Indirect</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Hashtags (comma-separated, for TikTok)
                      </label>
                      <input
                        type="text"
                        value={newBenchmark.hashtags}
                        onChange={(e) => setNewBenchmark({ ...newBenchmark, hashtags: e.target.value })}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="homedecor, neutraldecor"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                      >
                        ➕ Add Benchmark
                      </button>
                    </div>
                  </form>
                </div>

                <div className="bg-card rounded-lg shadow overflow-hidden border border-border">
                  <div className="p-4 bg-muted/50 border-b border-border flex justify-between items-center">
                    <h2 className="text-lg font-bold text-foreground">Benchmark Accounts ({benchmarks.length})</h2>
                    <button
                      onClick={() => exportToCSV(benchmarks, 'benchmarks.csv')}
                      className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                    >
                      📥 Export CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-foreground">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium">Platform</th>
                          <th className="px-4 py-2 text-left text-sm font-medium">Handle</th>
                          <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                          <th className="px-4 py-2 text-left text-sm font-medium">URL</th>
                          <th className="px-4 py-2 text-left text-sm font-medium">Added</th>
                        </tr>
                      </thead>
                      <tbody>
                        {benchmarks.map((b) => (
                          <tr key={b.id} className="border-t border-border hover:bg-muted/50">
                            <td className="px-4 py-2 text-sm">{b.platform}</td>
                            <td className="px-4 py-2 text-sm font-medium">{b.handle}</td>
                            <td className="px-4 py-2 text-sm">{b.type}</td>
                            <td className="px-4 py-2 text-sm">
                              <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                Link
                              </a>
                            </td>
                            <td className="px-4 py-2 text-sm text-muted-foreground">
                              {new Date(b.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'insights' && (
              <div className="bg-card rounded-lg shadow overflow-hidden border border-border">
                <div className="p-4 bg-muted/50 border-b border-border flex justify-between items-center">
                  <h2 className="text-lg font-bold text-foreground">Content Insights ({insights.length})</h2>
                  <button
                    onClick={() => exportToCSV(insights, 'insights.csv')}
                    className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                  >
                    📥 Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-foreground">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left">Account</th>
                        <th className="px-4 py-2 text-left">Pillar</th>
                        <th className="px-4 py-2 text-left">Format</th>
                        <th className="px-4 py-2 text-left">Hook</th>
                        <th className="px-4 py-2 text-left">Impressions</th>
                        <th className="px-4 py-2 text-left">ER %</th>
                        <th className="px-4 py-2 text-left">Takeaway</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insights.map((i) => (
                        <tr key={i.id} className="border-t border-border hover:bg-muted/50">
                          <td className="px-4 py-2">{i.account}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs">
                              {i.contentPillar || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-2">{i.format}</td>
                          <td className="px-4 py-2 max-w-xs truncate">{i.hook}</td>
                          <td className="px-4 py-2">{i.estimatedImpressions.toLocaleString()}</td>
                          <td className="px-4 py-2">{i.estimatedEngagementRate.toFixed(2)}%</td>
                          <td className="px-4 py-2 max-w-md truncate">
                            {i.enhancedTakeaway || i.keyTakeaway}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'schedule' && (
              <div className="bg-card rounded-lg shadow overflow-hidden border border-border">
                <div className="p-4 bg-muted/50 border-b border-border flex justify-between items-center">
                  <h2 className="text-lg font-bold text-foreground">Posting Schedule ({schedule.length})</h2>
                  <button
                    onClick={() => exportToCSV(schedule, 'posting_schedule.csv')}
                    className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                  >
                    📥 Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-foreground">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Platform</th>
                        <th className="px-4 py-2 text-left">Pillar</th>
                        <th className="px-4 py-2 text-left">Format</th>
                        <th className="px-4 py-2 text-left">Hook</th>
                        <th className="px-4 py-2 text-left">Visual Elements</th>
                        <th className="px-4 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map((s) => (
                        <tr key={s.id} className="border-t border-border hover:bg-muted/50">
                          <td className="px-4 py-2 font-medium">{s.date}</td>
                          <td className="px-4 py-2">{s.platform}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded text-xs">
                              {s.contentPillar}
                            </span>
                          </td>
                          <td className="px-4 py-2">{s.format}</td>
                          <td className="px-4 py-2 max-w-xs truncate">{s.hook}</td>
                          <td className="px-4 py-2 max-w-md truncate">{s.visualElements}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs capitalize">
                              {s.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'trends' && (
              <div className="bg-card rounded-lg shadow overflow-hidden border border-border">
                <div className="p-4 bg-muted/50 border-b border-border flex justify-between items-center">
                  <h2 className="text-lg font-bold text-foreground">Emerging Trends ({trends.length})</h2>
                  <button
                    onClick={() => exportToCSV(trends, 'trends.csv')}
                    className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                  >
                    📥 Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-foreground">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Value</th>
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Post Count</th>
                        <th className="px-4 py-2 text-left">Avg Engagement</th>
                        <th className="px-4 py-2 text-left">Detected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trends.map((t) => (
                        <tr key={t.id} className="border-t border-border hover:bg-muted/50">
                          <td className="px-4 py-2">
                            <span
                              className={`px-2 py-1 rounded text-xs ${t.type === 'hashtag'
                                ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
                                : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                                }`}
                            >
                              {t.type}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-medium">#{t.value}</td>
                          <td className="px-4 py-2">{t.name || '-'}</td>
                          <td className="px-4 py-2">{t.postCount}</td>
                          <td className="px-4 py-2">{t.avgEngagement.toFixed(0)}</td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {new Date(t.detectedAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'youtube' && (
              <div className="bg-card rounded-lg shadow overflow-hidden border border-border">
                <div className="p-4 bg-muted/50 border-b border-border flex justify-between items-center">
                  <h2 className="text-lg font-bold text-foreground">YouTube Creators ({youtubeVideos.length})</h2>
                  <button
                    onClick={() => exportToCSV(youtubeVideos, 'youtube_videos.csv')}
                    className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                  >
                    📥 Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-foreground">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left">Channel</th>
                        <th className="px-4 py-2 text-left">Title</th>
                        <th className="px-4 py-2 text-left">Published</th>
                        <th className="px-4 py-2 text-left">Views</th>
                        <th className="px-4 py-2 text-left">Likes</th>
                        <th className="px-4 py-2 text-left">Comments</th>
                        <th className="px-4 py-2 text-left">Transcript (preview)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {youtubeVideos.map((video) => (
                        <tr key={video.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <div className="flex flex-col">
                              <span className="font-semibold text-sm">{video.channelHandle || 'Channel'}</span>
                              {video.channelUrl && (
                                <a
                                  href={video.channelUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  View Channel
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2 max-w-xs">
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {video.title || 'Untitled video'}
                            </a>
                            {video.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{video.description}</p>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-muted-foreground">
                            {video.publishDate ? new Date(video.publishDate).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm">{video.views?.toLocaleString?.() || '—'}</td>
                          <td className="px-4 py-2 text-sm">{video.likes?.toLocaleString?.() || '—'}</td>
                          <td className="px-4 py-2 text-sm">{video.comments?.toLocaleString?.() || '—'}</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground max-w-sm">
                            {video.transcript
                              ? `${video.transcript.slice(0, 200)}${video.transcript.length > 200 ? '…' : ''}`
                              : 'No transcript'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
